import {IRegulationTimeFormatter} from "./regulation-time-formatter.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {NullableDate, NullableString} from "../../../common/types/nullable-types";
import {Check} from "@sparkware/app-utils";

export class RegulationStandardTimeFormatter implements IRegulationTimeFormatter {
    constructor(protected readonly services: IServiceFactoryExtended) {
    }

    formatClock(): string {
        const options: Intl.DateTimeFormatOptions = {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        };
        return new Intl.DateTimeFormat(undefined, options).format(this.services.time.getCurrentDate());
    }

    formatLocalDateWithFullTime(localDate: Date): string {
        const {day, month, year, hour, minute, second} = this._extractDateParts(localDate);
        return `${day}-${month}-${year} ${hour}:${minute}:${second}`;
    }

    formatLocalDateWithShortMonthAndTime(localDate: Date): string {
        const locale = this.services.sdk.getSkin().getCulture()
        const formatter = new Intl.DateTimeFormat(locale, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
        return formatter.format(localDate);
    }

    formatUtcDateWithFullTime(utcDate: NullableString): NullableString {
        let localDate = this._createLocalDateFromUtcDate(utcDate);
        if(!localDate) {
            return null;
        }

        return this.formatLocalDateWithFullTime(localDate);
    }

    formatUtcDateWithShortMonthAndTime(utcDate: NullableString): NullableString {
        let localDate = this._createLocalDateFromUtcDate(utcDate);
        if(!localDate) {
            return null;
        }

        return this.formatLocalDateWithShortMonthAndTime(localDate);
    }

    formatUnixTimeStampInSecondsWithFullTime(unixTimeStampInSeconds: number): string {
        const date = new Date(unixTimeStampInSeconds * 1000);
        return this.formatLocalDateWithFullTime(date);
    }


    formatLastLoginFromUtcDate(date: NullableString): NullableString {
        return this.formatUtcDateWithFullTime(date);
    }

    formatLastLoginFromUnixTimeStampInSeconds(lastLoginUnixTimeStampInSeconds: number): string {
        return this.formatUnixTimeStampInSecondsWithFullTime(lastLoginUnixTimeStampInSeconds);
    }

    formatHistoryRecordDate(utcDate: NullableString): NullableString {
        return this.formatUtcDateWithFullTime(utcDate);
    }

    protected _extractDateParts(date: Date) {
        return {
            day: date.getDate().toString().padStart(2, '0'),
            month:  (date.getMonth() + 1).toString().padStart(2, '0'),
            year: date.getFullYear().toString(),
            hour: date.getHours().toString().padStart(2, '0'),
            minute: date.getMinutes().toString().padStart(2, '0'),
            second:  date.getSeconds().toString().padStart(2, '0')
        }
    }


    protected _createLocalDateFromUtcDate(date: NullableString): NullableDate {
        if(!date) {
            return null;
        }

        if(!date.endsWith('Z')) {
            date += 'Z'; // Ensure the date is in UTC Zulu time format. This way the line below 'new Date(date)' will correctly convert the UTC date into the local date
        }

        const localDate = new Date(date);

        if(!Check.isDate(localDate)) {
            this.services.logger.error(`Invalid UTC date: ${date}`);
            return null;
        }

        return localDate;
    }

}